import { useState } from "react";

import { parsePresetInput } from "../lib/presets";
import { useSettings } from "../store/useSettings";

/**
 * Редактор пресетов размера.
 *
 * ОДНО текстовое поле, а не N спин-боксов, и это не экономия: переупорядочить и
 * удалить в тексте бесплатно, а прогон через normalizeSizes делает схлопывание
 * дублей и сортировку ВИДИМЫМИ до сохранения. Четыре числа не оправдывают
 * четырёх контролов со своим состоянием.
 *
 * Двухшагового подтверждения здесь нет намеренно: этот идиом в проекте
 * зарезервирован за действиями, которые тратят или раскрывают деньги (снятие
 * аварийного стопа, «снять все»), и размывать его на сохранение настроек
 * значит обесценить там, где он нужен.
 */
interface Props {
  sizes: string[];
  presign: string[] | null;
  /** Суммы оператора, которых трейдер пока не греет. */
  cold: string[];
  onClose: () => void;
}

export function SizePresetsEditor({ sizes, presign, cold, onClose }: Props) {
  const setSizes = useSettings((s) => s.setSizes);
  const [text, setText] = useState(() => sizes.join(" "));

  // Предпросмотр считается на каждый ввод: оператор видит, во что превратится
  // набранное, ДО того как нажмёт «сохранить».
  const preview = parsePresetInput(text);

  return (
    <div className="flex flex-col gap-2 rounded border border-[var(--color-border)] p-3">
      <label className="flex flex-col gap-1 text-xs text-[var(--color-muted)]">
        Суммы в долларах, через пробел или запятую
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          inputMode="decimal"
          placeholder="5 10 25"
          className="num rounded border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none focus:border-[var(--color-accent)]"
        />
      </label>

      <p className="num text-xs text-[var(--color-muted)]">
        будет: {preview.sizes.length > 0 ? preview.sizes.map((s) => `$${s}`).join(", ") : "— (список пуст)"}
      </p>

      {preview.notices.map((n, i) => (
        <p key={i} className="text-xs text-[var(--color-sell)]">
          {n.message}
        </p>
      ))}

      {/* Список трейдера здесь — не образец для сверки, а подтверждение: набор
          ему задаёт эта же панель, а показанное — то, что он РЕАЛЬНО заготовил.
          Он шире пресетов на ручную сумму, и это норма, а не расхождение. */}
      {presign !== null && (
        <p className="text-xs text-[var(--color-muted)]">
          трейдер греет:{" "}
          <span className="num">{presign.map((s) => `$${s}`).join(", ") || "пока ничего"}</span>
        </p>
      )}
      {presign === null && (
        <p className="text-xs text-[var(--color-muted)]">
          трейдер не сообщает список пред-подписанных сумм: пред-подпись выключена
          (PRESIGN_ENABLED) либо статус ещё не пришёл
        </p>
      )}
      {cold.length > 0 && (
        <p className="text-xs text-[var(--color-muted)]">
          пока не греет: <span className="num">{cold.map((s) => `$${s}`).join(", ")}</span> — набор
          доезжает до трейдера за секунду после сохранения
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setSizes(preview.sizes);
            onClose();
          }}
          disabled={preview.sizes.length === 0}
          className="rounded border border-[var(--color-accent)] px-2 py-1 text-xs disabled:opacity-40"
        >
          Сохранить
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-muted)]"
        >
          Отмена
        </button>
        {/* Кнопки «привести к списку трейдера» здесь больше нет, и это не
            упрощение. Она существовала, пока набор задавало окружение трейдера:
            согласовать их можно было только отказом оператора от своего набора.
            Теперь набор задаёт панель, и «принять список трейдера» означало бы
            принять свой же — плюс ручную сумму из поля ввода, которая стала бы
            кнопкой сама собой. */}
      </div>
    </div>
  );
}
